///
/// EarthBrowser
///
/// Created by Matt Giger on 8/21/2024
/// Copyright (c) 2024 Geodata Labs
///

import { RenderBuffer } from "@stovokor/shader/RenderBuffer"
import { Texture } from "@stovokor/shader/Texture"
import { StovokorContext } from "@stovokor/StovokorContext"

export enum FramebufferAttachments {
	none		= 0x00,
	color		= 0x01,
	depth		= 0x02,
	stencil		= 0x04
}

/**
 * Represents a WebGL framebuffer object.
 *
 * This class provides methods to create, manage, and interact with a WebGL framebuffer.
 * It supports attaching color and depth buffers, binding and unbinding the framebuffer,
 * and proper disposal of resources.
 *
 * Note: You will need to add layouts to your fragment shader like:
 * 	layout(location = 0) out vec4 color;
 * 	layout(location = 1) out vec4 depth;
 * 	layout(location = 2) out vec3 normal_tangentspace; // or whatever
 *
 * @class FrameBuffer
 * @property {FramebufferAttachments} attachments - Bitwise flag indicating the types of attachments added to this framebuffer.
 */
export class FrameBuffer {
	private context: StovokorContext

	/** The WebGL framebuffer object. */
	private framebuffer: WebGLFramebuffer | null

	/** Bitwise flag indicating the types of attachments added to this framebuffer. */
	public attachments: FramebufferAttachments = FramebufferAttachments.none

	// Color attachment points in use, for drawBuffers (which only accepts
	// COLOR_ATTACHMENTi / NONE; the depth attachment must not be listed)
	private colorAttachments: GLenum[] = []
	// Completeness and the draw-buffer list are properties of the
	// attachment set, so they are (re)established on the first bind after
	// an attach rather than on every bind: checkFramebufferStatus is a
	// synchronous driver round trip
	private validated: boolean = false

	/**
	 * Creates a new FrameBuffer instance.
	 * Initializes a WebGL framebuffer object.
	 */
	constructor(context: StovokorContext) {
		this.context = context
		this.framebuffer = context.gl.createFramebuffer()
	}

	/**
	 * Destroys the framebuffer, releasing WebGL resources.
	 */
	public destroy() {
		if(this.framebuffer) {
			this.context.gl.deleteFramebuffer(this.framebuffer)
			this.framebuffer = null
		}
	}

	/**
	 * Attaches a buffer (RenderBuffer or Texture) to the framebuffer.
	 * framebufferTexture2D/framebufferRenderbuffer act on the currently bound
	 * framebuffer, so this binds ours for the call and restores the default
	 * binding afterwards.
	 * @param attachment - The attachment point (e.g., COLOR_ATTACHMENT0, DEPTH_ATTACHMENT).
	 * @param buffer - The buffer to attach (either a RenderBuffer or Texture).
	 */
	public attach(attachment: GLenum, buffer: RenderBuffer | Texture) {
		const gl = this.context.gl
		gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, this.framebuffer)

		// Classify by the attachment point, not the object type: a Texture
		// can carry depth (DEPTH_COMPONENT24) and a RenderBuffer colour
		switch(attachment) {
			case WebGL2RenderingContext.DEPTH_ATTACHMENT:
				this.attachments |= FramebufferAttachments.depth
				break
			case WebGL2RenderingContext.STENCIL_ATTACHMENT:
				this.attachments |= FramebufferAttachments.stencil
				break
			case WebGL2RenderingContext.DEPTH_STENCIL_ATTACHMENT:
				this.attachments |= FramebufferAttachments.depth | FramebufferAttachments.stencil
				break
			default:
				this.attachments |= FramebufferAttachments.color
				if(!this.colorAttachments.includes(attachment)) {
					this.colorAttachments.push(attachment)
					this.colorAttachments.sort((a, b) => a - b)
				}
		}

		if(buffer instanceof RenderBuffer) {
			gl.framebufferRenderbuffer(WebGL2RenderingContext.FRAMEBUFFER, attachment, WebGL2RenderingContext.RENDERBUFFER, buffer.bufferId)
		} else {
			gl.framebufferTexture2D(WebGL2RenderingContext.FRAMEBUFFER, attachment, WebGL2RenderingContext.TEXTURE_2D, buffer.textureId, 0)
		}
		gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, null)
		this.validated = false
	}

	/**
	 * Binds the framebuffer for rendering. On the first bind after the
	 * attachments changed, sets the draw buffers and checks completeness.
	 * @throws {Error} If the framebuffer is not complete.
	 */
	public bind() {
		const gl = this.context.gl
		gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, this.framebuffer)

		if(!this.validated) {
			// drawBuffers slot i must be COLOR_ATTACHMENTi or NONE
			const slots: GLenum[] = []
			if(this.colorAttachments.length > 0) {
				const last = this.colorAttachments[this.colorAttachments.length - 1]
				for(let point = WebGL2RenderingContext.COLOR_ATTACHMENT0; point <= last; point++) {
					slots.push(this.colorAttachments.includes(point) ? point : WebGL2RenderingContext.NONE)
				}
			} else {
				slots.push(WebGL2RenderingContext.NONE)
			}
			gl.drawBuffers(slots)
			if(gl.checkFramebufferStatus(WebGL2RenderingContext.FRAMEBUFFER) != WebGL2RenderingContext.FRAMEBUFFER_COMPLETE) {
				throw new Error("Framebuffer is not complete")
			}
			this.validated = true
		}
	}

	/** Bind and unbind once so an incomplete attachment set throws now rather than at first use */
	public validate(): void {
		this.bind()
		this.unbind()
	}

	/**
	 * Unbinds the framebuffer, reverting to the default framebuffer.
	 */
	public unbind() {
		this.context.gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, null)
	}
}